import { UPDATE_DECREE_MUTATION } from "@/services/web/api-decree.const";
import { SortOrder } from "@/types/gql/graphql";
import { useTrackedMutation } from "@/hooks/apollo-insights-hooks";
import { useContext, useEffect, useState } from "react";
import { CityContext } from "contexts/city-context-provider";
import { useQueryDecreesPaginated, useQueryTemporaryDecreesPaginated } from "./use-query-decree-file";
import { PaginatedDecreeArgs, UpdateDecreeInput } from "types/decree.type";

export const paramsDefault: PaginatedDecreeArgs = {
  cityId: "",
  pageIndex: 1,
  pageSize: 10,
  sortField: "createdAt",
  sortOrder: SortOrder.Desc,
  filter: [],
};

export const useDecree = () => {
  const { currentCity } = useContext(CityContext);
  const [decreeParams, setDecreeParams] = useState<PaginatedDecreeArgs>({
    ...paramsDefault,
  });

  useEffect(() => {
    if (currentCity?.id) {
      setDecreeParams({
        ...paramsDefault,
        cityId: currentCity.id,
      });
    }
  }, [currentCity]);

  // extension is a temporary-decree-only field, so the general query can't filter on it.
  const isExtensionFilter = decreeParams.filter?.some((f) => f.field === "extension") ?? false;
  const general = useQueryDecreesPaginated(decreeParams, { skip: isExtensionFilter });
  const temporary = useQueryTemporaryDecreesPaginated(decreeParams, { skip: !isExtensionFilter });
  const { decreeData, isLoading, refetchDecree } = isExtensionFilter ? temporary : general;

  return { decreeData, isLoading, setDecreeParams, refetchDecree, decreeParams };
};

export const useUpdateDecree = (refetchDecree: () => void) => {
  const [updateTemporaryDecree] = useTrackedMutation(UPDATE_DECREE_MUTATION, {
    actionName: "UpdateDecree",
  });

  const updateDecree = async (input: UpdateDecreeInput) => {
    await updateTemporaryDecree({
      variables: { updateDecreeInput: input },
      onCompleted: () => {
        refetchDecree();
      },
    });
  };

  return {
    updateDecree,
  };
};
